/**
 * Cart Policy Configuration
 * 
 * Data Layer 职责：从 window 全局配置读取购物车政策配置
 * - 最小订单金额
 * - COD 可用门槛
 * 
 * 所有金额单位：cents（分）
 */

/**
 * 购物车政策配置接口
 */
export interface CartPolicyConfig {
    /** 最小下单金额 (分) */
    minOrderCents: number;
    /** 货到付款 (COD) 可用门槛 (分) */
    codThresholdCents: number;
    /** 发货截单时间 (小时, 0-23) */
    cutoffHour: number;
}

export const DEFAULT_FREE_SHIPPING_THRESHOLD_CENTS = 4900;

/**
 * Holiday Configuration
 * 
 * 节假日配置，用于发货日期计算
 * - recurring: 每年循环节日（MM-DD 格式，如 "12-25" 代表圣诞节）
 * - specific: 特定日期节日（YYYY-MM-DD 格式，如 "2025-12-24"）
 */
/**
 * 节假日配置接口，用于发货日期计算逻辑
 */
export interface HolidayConfig {
    /** 每年循环节日 (MM-DD 格式，如 "12-25") */
    recurring: string[];
    /** 特定日期节日 (YYYY-MM-DD 格式，如 "2023-12-25") */
    specific: string[];
}


declare global {
    interface Window {
        __CART_CONFIG__?: {
            min_order_cents?: number;
            cod_threshold_cents?: number;
            shipping_cutoff_hour?: number;
            shipping_holidays?: string[];  // 从 Liquid 注入的节假日列表
            free_shipping_threshold_cents?: number;
        };
    }
}

/**
 * 从 window 全局配置读取购物车政策配置
 * 
 * Liquid 注入示例（由主题层负责）：
 * ```liquid
 * <script>
 *   window.__CART_CONFIG__ = {
 *     min_order_cents: {{ settings.min_order_amount | default: 15 | times: 100 }},
 *     cod_threshold_cents: {{ settings.cod_threshold | default: 25 | times: 100 }}
 *   };
 * </script>
 * ```
 */
export function getCartPolicyConfigFromWindow(): CartPolicyConfig {
    const globalConfig = (typeof window !== 'undefined' && window.__CART_CONFIG__) || {};

    return {
        minOrderCents:
            typeof globalConfig.min_order_cents === 'number'
                ? globalConfig.min_order_cents
                : 1500,  // 默认 15€
        codThresholdCents:
            typeof globalConfig.cod_threshold_cents === 'number'
                ? globalConfig.cod_threshold_cents
                : 2500,  // 默认 25€
        cutoffHour:
            typeof globalConfig.shipping_cutoff_hour === 'number'
                ? globalConfig.shipping_cutoff_hour
                : 16,     // 默认 16:00
    };
}

/**
 * 从 window 全局配置读取免邮门槛
 */
export function getFreeShippingThresholdFromWindow(): number {
    const globalConfig = (typeof window !== 'undefined' && window.__CART_CONFIG__) || {};
    const raw = globalConfig.free_shipping_threshold_cents;
    if (typeof raw !== 'number') return DEFAULT_FREE_SHIPPING_THRESHOLD_CENTS;
    // Backward-compatible: treat < 1000 as euros, otherwise cents.
    return raw >= 1000 ? raw : Math.round(raw * 100);
}

/**
 * 从 window 全局配置读取节假日配置
 * 
 * Data Layer 职责：将 Liquid 注入的原始数组转换为类型安全的配置对象
 * 
 * Liquid 注入示例（由主题层负责）：
 * ```liquid
 * {%- assign shipping_holidays = '[]' -%}
 * {%- if shop.metafields.custom.policy_shipping_holiday != blank -%}
 *   {%- assign shipping_holidays = shop.metafields.custom.policy_shipping_holiday.value | json -%}
 * {%- endif -%}
 * 
 * <script>
 *   window.__CART_CONFIG__ = window.__CART_CONFIG__ || {};
 *   window.__CART_CONFIG__.shipping_holidays = {{ shipping_holidays }};
 * </script>
 * ```
 * 
 * @returns HolidayConfig 对象，异常情况返回空数组
 */
export function getHolidayConfigFromWindow(): HolidayConfig {
    // SSR 安全检查
    if (typeof window === 'undefined') {
        return { recurring: [], specific: [] };
    }

    const rawHolidays = (window as any).__CART_CONFIG__?.shipping_holidays;

    // 数据验证：必须是数组
    if (!Array.isArray(rawHolidays)) {
        return { recurring: [], specific: [] };
    }

    return {
        recurring: [],  // 预留字段，当前未使用
        specific: rawHolidays,  // 将 Liquid 注入的日期列表放入 specific
    };
}
